// Tabla de clasificación general y por liga
import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Trophy, Medal, TrendingUp, TrendingDown, Minus,
  Search, Filter, ChevronUp, Crown,
} from 'lucide-react'
import Card from '../components/ui/Card'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
import Button from '../components/ui/Button'
import Skeleton from '../components/ui/Skeleton'
import useAuthStore from '../store/useAuthStore'

// Datos de ejemplo para la clasificación
const mockLeaderboard = [
  { rank: 1, name: 'Carlos Martínez', points: 287, accuracy: '78%', trend: 'up', change: 2 },
  { rank: 2, name: 'María González', points: 265, accuracy: '74%', trend: 'up', change: 1 },
  { rank: 3, name: 'Andrés López', points: 251, accuracy: '71%', trend: 'down', change: -1 },
  { rank: 4, name: 'Sofía Rodríguez', points: 243, accuracy: '69%', trend: 'same', change: 0 },
  { rank: 5, name: 'Diego Hernández', points: 238, accuracy: '68%', trend: 'up', change: 3 },
  { rank: 6, name: 'Valentina Torres', points: 229, accuracy: '66%', trend: 'down', change: -2 },
  { rank: 7, name: 'Mateo Ramírez', points: 221, accuracy: '65%', trend: 'up', change: 1 },
  { rank: 8, name: 'Isabella Flores', points: 215, accuracy: '63%', trend: 'same', change: 0 },
  { rank: 9, name: 'Sebastián Díaz', points: 208, accuracy: '61%', trend: 'down', change: -3 },
  { rank: 10, name: 'Camila Vargas', points: 201, accuracy: '60%', trend: 'up', change: 2 },
  { rank: 11, name: 'Lucas Morales', points: 195, accuracy: '58%', trend: 'same', change: 0 },
  { rank: 12, name: 'Paula Jiménez', points: 189, accuracy: '57%', trend: 'up', change: 1 },
]

import { useCommunities } from '../hooks/useCommunities'

// Icono de medalla según posición
function RankIcon({ rank }) {
  if (rank === 1) return <Crown className="w-5 h-5 text-warning flex justify-center mx-auto" />
  if (rank === 2) return <Medal className="w-5 h-5 text-text-secondary flex justify-center mx-auto" />
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-700 flex justify-center mx-auto" />
  return <span className="font-mono text-sm font-bold text-text-muted w-5 text-center block mx-auto">{rank}</span>
}

// Icono de tendencia
function TrendIcon({ trend, change }) {
  if (trend === 'up') return (
    <span className="flex items-center gap-0.5 text-xs text-success">
      <TrendingUp className="w-3 h-3" /> +{change}
    </span>
  )
  if (trend === 'down') return (
    <span className="flex items-center gap-0.5 text-xs text-error">
      <TrendingDown className="w-3 h-3" /> {change}
    </span>
  )
  return (
    <span className="flex items-center gap-0.5 text-xs text-text-muted">
      <Minus className="w-3 h-3" /> —
    </span>
  )
}

export default function Leaderboard() {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('global')
  const [selectedLeagueId, setSelectedLeagueId] = useState(null)
  
  const { user } = useAuthStore()
  const {
    communities: leagues,
    isLoadingCommunities: isLoadingLeagues,
    useCommunity: useLeague,
    leaderboard: globalLeaderboard,
    isLoadingLeaderboard,
  } = useCommunities()

  // Si no hay liga seleccionada pero el usuario tiene ligas, seleccionar la primera por defecto
  if (activeTab === 'league' && !selectedLeagueId && leagues.length > 0) {
    setSelectedLeagueId(leagues[0].id)
  }

  // Cargar el ranking de la liga si corresponde
  const { data: leagueRankData, isLoading: isLoadingLeagueRank } = useLeague(selectedLeagueId)
  const leagueRanking = leagueRankData?.datos?.ranking || []

  // Decidir qué ranking mostrar
  const rawData = activeTab === 'global' ? globalLeaderboard : leagueRanking
  const isLoading = activeTab === 'global' ? isLoadingLeaderboard : isLoadingLeagueRank

  // Formatear filas para la vista
  const formattedEntries = rawData.map((item, index) => ({
    rank: item.posicion || item.posicion_liga || (index + 1),
    name: `${item.nombre} ${item.apellido}`,
    points: Number(item.puntos || 0),
    accuracy: '—',
    trend: 'same',
    change: 0,
    usuario_id: item.usuario_id || item.id,
  }))

  // Filtrar por búsqueda
  const filteredLeaderboard = formattedEntries.filter(entry =>
    entry.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Obtener posición y datos del usuario actual
  const userRankItem = formattedEntries.find(e => Number(e.usuario_id) === Number(user?.id))
  const userRank = userRankItem || {
    rank: '-',
    name: user ? `${user.nombre} ${user.apellido}` : 'Tú',
    points: 0,
    accuracy: '—',
    trend: 'same',
    change: 0,
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Encabezado */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary flex items-center gap-3">
          <Trophy className="w-7 h-7 text-warning" />
          Clasificación
        </h1>
        <p className="text-text-secondary mt-1">
          Posiciones actualizadas en tiempo real con cada resultado del Mundial.
        </p>
      </motion.div>

      {/* Tu posición destacada */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <Card variant="cyan" padding="md" className="glow-cyan">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-cyan/20 flex items-center justify-center">
              <span className="font-mono text-xl font-bold text-cyan">
                #{userRank.rank}
              </span>
            </div>
            <div className="flex-1">
              <p className="font-display font-semibold text-text-primary">Tu posición</p>
              <p className="text-sm text-text-secondary">
                {userRank.points} puntos · {activeTab === 'global' ? 'Ranking Global' : 'Ranking de la Comunidad'}
              </p>
            </div>
            <TrendIcon trend={userRank.trend} change={userRank.change} />
          </div>
        </Card>
      </motion.div>

      {/* Pestañas y búsqueda */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'global' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('global')}
          >
            Global
          </Button>
          <Button
            variant={activeTab === 'league' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('league')}
          >
            Mis comunidades
          </Button>
        </div>
        <div className="flex-1">
          <Input
            icon={Search}
            placeholder="Buscar jugador..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Selector de comunidad privada si está activa la pestaña de comunidades */}
      {activeTab === 'league' && (
        <div className="mb-6 bg-surface-card/40 border border-border-subtle p-4 rounded-2xl">
          {isLoadingLeagues ? (
            <p className="text-xs text-text-muted">Cargando tus comunidades...</p>
          ) : leagues.length > 0 ? (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <label className="text-xs text-text-muted uppercase tracking-wider font-semibold block mb-1">
                  Selecciona una comunidad
                </label>
                <select
                  value={selectedLeagueId || ''}
                  onChange={(e) => setSelectedLeagueId(Number(e.target.value))}
                  className="w-full sm:w-64 bg-navy-light text-text-primary border border-border-subtle rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-cyan"
                >
                  {leagues.map(l => (
                    <option key={l.id} value={l.id}>{l.nombre}</option>
                  ))}
                </select>
              </div>
              {selectedLeagueId && (
                <div className="text-xs text-text-muted font-mono self-end">
                  Código de invitación: <span className="text-cyan font-bold">{leagues.find(l => l.id === selectedLeagueId)?.codigo}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-text-muted mb-2">No eres miembro de ninguna comunidad privada.</p>
              <Button size="sm" onClick={() => window.location.href = (window.__APP_BASE_URL__ || '/') + 'comunidades'}>
                Crear o unirse a una comunidad
              </Button>
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3 py-6">
          <Skeleton className="h-10 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
        </div>
      ) : activeTab === 'league' && leagues.length === 0 ? (
        null
      ) : (
        <>
          {/* Podio (top 3) */}
          {filteredLeaderboard.length >= 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-3 gap-3 mb-6"
            >
              {/* Segundo lugar */}
              <div className="flex flex-col items-center pt-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-surface-card border-2 border-text-secondary flex items-center justify-center mb-2">
                  <span className="text-lg sm:text-xl font-bold text-text-secondary">2</span>
                </div>
                <p className="text-xs sm:text-sm font-medium text-text-primary text-center truncate w-full">
                  {filteredLeaderboard[1]?.name || '—'}
                </p>
                <p className="font-mono text-sm font-bold text-text-secondary">
                  {filteredLeaderboard[1] ? `${filteredLeaderboard[1].points} pts` : ''}
                </p>
              </div>

              {/* Primer lugar */}
              <div className="flex flex-col items-center">
                <Crown className="w-6 h-6 text-warning mb-1" />
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-warning/10 border-2 border-warning flex items-center justify-center mb-2 glow-cyan">
                  <span className="text-xl sm:text-2xl font-bold text-warning">1</span>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-text-primary text-center truncate w-full">
                  {filteredLeaderboard[0]?.name || '—'}
                </p>
                <p className="font-mono text-sm font-bold text-warning">
                  {filteredLeaderboard[0] ? `${filteredLeaderboard[0].points} pts` : ''}
                </p>
              </div>

              {/* Tercer lugar */}
              <div className="flex flex-col items-center pt-8">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-surface-card border-2 border-amber-700 flex items-center justify-center mb-2">
                  <span className="text-lg sm:text-xl font-bold text-amber-700">3</span>
                </div>
                <p className="text-xs sm:text-sm font-medium text-text-primary text-center truncate w-full">
                  {filteredLeaderboard[2]?.name || '—'}
                </p>
                <p className="font-mono text-sm font-bold text-amber-700">
                  {filteredLeaderboard[2] ? `${filteredLeaderboard[2].points} pts` : ''}
                </p>
              </div>
            </motion.div>
          )}

          {/* Tabla de clasificación completa */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Card variant="default" padding="none">
              {/* Encabezado de tabla */}
              <div className="grid grid-cols-[3rem_1fr_5rem_4rem_3.5rem] sm:grid-cols-[3rem_1fr_5rem_5rem_4rem] gap-2 px-4 py-3 border-b border-border-subtle text-xs font-semibold text-text-muted uppercase tracking-wider">
                <span>#</span>
                <span>Jugador</span>
                <span className="text-right">Puntos</span>
                <span className="text-right hidden sm:block">Precisión</span>
                <span className="text-right">Tend.</span>
              </div>

              {/* Filas */}
              {filteredLeaderboard.map((entry, i) => {
                const isMe = Number(entry.usuario_id) === Number(user?.id)
                return (
                  <motion.div
                    key={entry.usuario_id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className={`
                      grid grid-cols-[3rem_1fr_5rem_4rem_3.5rem] sm:grid-cols-[3rem_1fr_5rem_5rem_4rem] gap-2 
                      px-4 py-3 items-center border-b border-border-subtle/50
                      hover:bg-surface-card transition-colors
                      ${isMe ? 'bg-cyan/10 border-y border-cyan/20 font-semibold' : ''}
                      ${entry.rank <= 3 && !isMe ? 'bg-surface-card/30' : ''}
                    `}
                  >
                    <RankIcon rank={entry.rank} />
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-cyan/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-cyan">
                          {entry.name.charAt(0)}
                        </span>
                      </div>
                      <span className={`text-sm truncate ${isMe ? 'text-cyan font-semibold' : 'text-text-primary'}`}>
                        {isMe ? 'Tú' : entry.name}
                      </span>
                    </div>
                    <span className="text-right font-mono text-sm font-semibold text-cyan">
                      {entry.points}
                    </span>
                    <span className="text-right font-mono text-xs text-text-muted hidden sm:block">
                      {entry.accuracy}
                    </span>
                    <div className="flex justify-end">
                      <TrendIcon trend={entry.trend} change={entry.change} />
                    </div>
                  </motion.div>
                )
              })}

              {filteredLeaderboard.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-sm text-text-muted">No se encontraron resultados</p>
                </div>
              )}
            </Card>
          </motion.div>
        </>
      )}
    </div>
  )
}
